(() => {
  const lifecycleKey = 'dessonScrollVideoHeroLifecycle';
  const existingLifecycle = window[lifecycleKey];
  if (existingLifecycle) {
    existingLifecycle.initialize(document);
    return;
  }

  const selector = '[data-desson-scroll-video-hero]';
  const framesPerSecond = 30;
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const quantizeTime = (time, duration) => clamp(
    Math.round(time * framesPerSecond) / framesPerSecond,
    0,
    duration
  );

  const sectionProgress = (rect, viewportHeight) => {
    const distance = Math.max(1, rect.height - viewportHeight);
    return clamp(-rect.top / distance);
  };

  class DessonScrollVideoHero {
    constructor(root) {
      this.root = root;
      this.video = root.querySelector('[data-desson-scroll-video]');
      this.card = root.querySelector('[data-desson-product-card]');
      this.threshold = Number(root.dataset.revealThreshold || 0.78);
      this.progress = 0;
      this.targetTime = 0;
      this.frame = 0;
      this.measureFrame = 0;
      this.active = false;
      this.fallback = false;
      this.permanentFallback = false;
      this.started = false;
      this.workStarted = false;
      this.videoRequested = false;
      this.productVisible = null;
      this.observer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = () => this.scheduleMeasure();
      this.onResize = () => this.scheduleMeasure();
      this.onMetadata = () => {
        if (this.fallback) return;
        this.scheduleMeasure();
      };
      this.onData = () => {
        if (!this.fallback && this.video.readyState >= 2) {
          this.root.classList.add('is-media-ready');
        }
      };
      this.onError = () => this.setFallback(true);
      this.onSeeked = () => {
        this.onData();
        if (this.active && !this.fallback && !this.frame) {
          this.frame = requestAnimationFrame(() => this.render());
        }
      };
      this.onReducedMotionChange = (event) => {
        if (event.matches) this.setFallback(false);
        else this.resume();
      };
    }

    setProductVisible(visible) {
      if (this.productVisible === visible) return;
      this.productVisible = visible;
      this.root.classList.toggle('is-product-visible', visible);
      if (!this.card) return;

      this.card.inert = !visible;
      if (visible) {
        this.card.removeAttribute('tabindex');
        this.card.removeAttribute('aria-hidden');
        this.card.removeAttribute('inert');
      } else {
        this.card.setAttribute('tabindex', '-1');
        this.card.setAttribute('aria-hidden', 'true');
        this.card.setAttribute('inert', '');
      }
    }

    setEditorialProgress(progress) {
      const normalized = clamp(progress, 0, 1);
      this.root.style.setProperty(
        '--desson-hero-progress',
        String(Math.round(normalized * 1000) / 1000)
      );
    }

    hasUsableVideo() {
      return Boolean(
        this.video
        && this.video.readyState >= 1
        && Number.isFinite(this.video.duration)
        && this.video.duration > 0
      );
    }

    addReducedMotionListener() {
      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
      } else {
        this.reducedMotion.addListener?.(this.onReducedMotionChange);
      }
    }

    removeReducedMotionListener() {
      if (this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      } else {
        this.reducedMotion.removeListener?.(this.onReducedMotionChange);
      }
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.setProductVisible(false);
      this.addReducedMotionListener();

      if (!this.video) {
        this.setFallback(true);
      } else if (this.reducedMotion.matches) {
        this.setFallback(false);
      } else {
        this.resume();
      }
    }

    resume() {
      if (this.permanentFallback || this.workStarted || !this.video) return;
      this.fallback = false;
      this.root.classList.remove('is-fallback');
      this.setEditorialProgress(0);
      this.setProductVisible(false);
      this.video.pause();
      this.video.addEventListener('loadedmetadata', this.onMetadata);
      this.video.addEventListener('loadeddata', this.onData);
      this.video.addEventListener('error', this.onError);
      this.video.addEventListener('seeked', this.onSeeked);

      this.observer = new IntersectionObserver((entries) => {
        const entry = entries.find(({ target }) => target === this.root);
        this.active = Boolean(entry?.isIntersecting);
        if (this.active) {
          this.measure();
        } else {
          this.cancelWorkFrames();
        }
      }, { rootMargin: '100% 0px' });
      this.observer.observe(this.root);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.workStarted = true;

      if (this.hasUsableVideo()) this.onMetadata();
      this.onData();
    }

    requestVideo() {
      if (this.videoRequested || this.fallback || !this.video) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const source = mobile
        ? this.video.dataset.mobileSrc || this.video.dataset.desktopSrc
        : this.video.dataset.desktopSrc || this.video.dataset.mobileSrc;
      if (!source) {
        this.setFallback(true);
        return;
      }
      this.videoRequested = true;
      this.video.preload = 'auto';
      this.video.src = source;
      this.video.load();
    }

    stopWork() {
      if (this.workStarted) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.video?.removeEventListener('loadedmetadata', this.onMetadata);
        this.video?.removeEventListener('loadeddata', this.onData);
        this.video?.removeEventListener('error', this.onError);
        this.video?.removeEventListener('seeked', this.onSeeked);
        this.observer?.disconnect();
      }
      this.observer = null;
      this.workStarted = false;
      this.active = false;
      this.cancelWorkFrames();
    }

    cancelWorkFrames() {
      if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.measureFrame = 0;
      this.frame = 0;
    }

    setFallback(permanent = true) {
      if (permanent) this.permanentFallback = true;
      this.fallback = true;
      this.stopWork();
      this.setEditorialProgress(0);
      this.root.classList.add('is-fallback');
      this.setProductVisible(true);
    }

    scheduleMeasure() {
      if (!this.active || this.fallback || this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measure();
      });
    }

    measure() {
      if (!this.active || this.fallback) return;
      const rect = this.root.getBoundingClientRect();
      this.progress = sectionProgress(rect, window.innerHeight);
      this.setEditorialProgress(this.progress);
      this.setProductVisible(this.progress >= this.threshold);

      if (this.progress > 0) this.requestVideo();

      if (!this.hasUsableVideo()) return;
      this.targetTime = quantizeTime(this.progress * this.video.duration, this.video.duration);
      if (!this.frame) this.frame = requestAnimationFrame(() => this.render());
    }

    render() {
      this.frame = 0;
      if (!this.active || this.fallback || !this.hasUsableVideo()) return;
      if (this.video.seeking) return;

      const delta = this.targetTime - this.video.currentTime;
      if (Math.abs(delta) > 0.04) {
        const response = Math.abs(delta) > 1.2 ? 0.72 : 0.42;
        this.video.currentTime += delta * response;
      } else {
        this.video.currentTime = this.targetTime;
      }
    }

    destroy() {
      this.stopWork();
      this.removeReducedMotionListener();
      this.root.style.removeProperty('--desson-hero-progress');
      this.started = false;
      delete this.root.dessonScrollVideoHero;
    }
  }

  const sectionRoots = (container) => {
    const roots = [];
    if (container.matches?.(selector)) roots.push(container);
    roots.push(...container.querySelectorAll(selector));
    return roots;
  };

  const initialize = (container = document) => {
    sectionRoots(container).forEach((root) => {
      if (root.dessonScrollVideoHero) return;
      root.dessonScrollVideoHero = new DessonScrollVideoHero(root);
      root.dessonScrollVideoHero.start();
    });
  };

  const lifecycle = { initialize };
  window[lifecycleKey] = lifecycle;

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    sectionRoots(event.target).forEach((root) => root.dessonScrollVideoHero?.destroy());
  });
})();
